import { notifications } from '@mantine/notifications'
import { useCallback, useState } from 'react'

import { usePollingQuery } from '@/hooks/usePollingQuery'
import type { PaymentMethod } from '@/services/api/api.types'
import { hasPaymentMethodChanged } from '@/utils/paymentMethodHelpers'

interface UsePaymentMethodUpdateProps {
  onUpdateComplete?: () => void
}

export function usePaymentMethodUpdate({ onUpdateComplete }: UsePaymentMethodUpdateProps = {}) {
  const { pollUntilCondition } = usePollingQuery()
  const [isPolling, setIsPolling] = useState(false)

  // Polling strategy to wait for payment method to be updated
  const pollForPaymentMethodUpdate = useCallback(
    async (previousPaymentMethod: PaymentMethod | null) => {
      return await pollUntilCondition({
        queryKey: ['billing', 'info'],
        condition: (data: any) => {
          const updatedBillingInfo = data?.billingInfo
          const updatedPaymentMethod = updatedBillingInfo?.paymentMethod

          return hasPaymentMethodChanged(previousPaymentMethod, updatedPaymentMethod)
        },
        logContext: 'Payment method update polling',
      })
    },
    [pollUntilCondition],
  )

  // Handle payment method update success with polling
  const handlePaymentMethodUpdateSuccess = useCallback(
    async (isAddingNew: boolean, previousPaymentMethod: PaymentMethod | null = null) => {
      try {
        setIsPolling(true)

        // Poll for payment method update
        const pollSuccess = await pollForPaymentMethodUpdate(previousPaymentMethod)

        // Show appropriate notification
        const successMessage = isAddingNew ? 'Payment method added successfully' : 'Payment method updated successfully'

        if (pollSuccess) {
          notifications.show({
            title: 'Success',
            message: successMessage,
            color: 'green',
          })
        } else {
          notifications.show({
            title: 'Update Processing',
            message: `${successMessage.replace('successfully', '')}. Changes may take a few moments to appear.`,
            color: 'blue',
          })
        }

        // Call completion handler
        onUpdateComplete?.()
      } catch (error) {
        console.error('Failed to handle payment method update completion:', error)
        notifications.show({
          title: 'Update Error',
          message:
            'There was an issue completing the payment method update. Please contact support if the problem persists.',
          color: 'red',
        })
      } finally {
        setIsPolling(false)
      }
    },
    [pollForPaymentMethodUpdate, onUpdateComplete],
  )

  return {
    handlePaymentMethodUpdateSuccess,
    isPolling,
  }
}
